import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('popstate', this.#handleBrowserHistoryClose);
    window.addEventListener('pageshow', this.#handleBrowserHistoryClose);
    this.refs.dialog?.addEventListener('cancel', this.#handleDialogCancel);
    this.refs.dialog?.addEventListener('close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('popstate', this.#handleBrowserHistoryClose);
    window.removeEventListener('pageshow', this.#handleBrowserHistoryClose);
    this.refs.dialog?.removeEventListener('cancel', this.#handleDialogCancel);
    this.refs.dialog?.removeEventListener('close', this.#handleDialogClose);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleDialogCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleDialogClose = () => {
    window.setTimeout(this.#resetClosedState, 0);
  };

  #handleBrowserHistoryClose = () => {
    window.setTimeout(() => {
      if (!this.refs.dialog?.open) {
        this.#resetClosedState();
      }
    }, 0);
  };

  #resetClosedState = () => {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    dialog.classList.remove('dialog-closing');
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
  };

  open(event) {
    event?.stopPropagation?.();
    this.#resetClosedState();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
    window.setTimeout(this.#resetClosedState, 260);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
